import { Box, Divider, Grid, Paper, Tab, Tabs } from '@mui/material';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import MainContentHeader from 'src/shared/components/atoms/mainContentHeader';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import { InferType } from 'yup';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AvailablePaymentMethodsEnum, hasFlag } from 'src/shared/models/enums/availablePaymentMethods.enum';
import { AssociationAccountTypeEnum, getAssociationAccountTypeDescription } from 'src/shared/models/enums/associationAccountType.enum';
import { useSchemaInvoiceGroup } from '../schemas/useSchemaInvoiceGroup';
import { PaymentMethodsConfiguration } from '../components/paymentMethodsConfiguration';
import { ActivePaymentOptionsInfo } from '../components/activePaymentOptionsInfo';

export const AdminInvoiceGroupDetailPage = () => {
  const params = useParams();
  const [loading, setLoading] = useState(true);
  const isMounted = useIsMounted();

  const [invoiceGroup, setInvoiceGroup] = useState<InvoiceGroup>();

  const invoiceGroupSchema = useSchemaInvoiceGroup();
  type InvoiceGroupInterface = InferType<typeof invoiceGroupSchema>;

  const formMethods = useForm<InvoiceGroupInterface>({
    resolver: yupResolver(invoiceGroupSchema),
    mode: 'all',
    defaultValues: {
      swishAccount: '',
      swishData1: '',
      swishActive: '0',

      // Stripe
      stripeAccount: '',
      stripeActive: '0',

      // Bank
      accountNr: '',
      accountType: -1, // okänd typ
      accountName: '',
      iban: '',
      bic: '',

      // Autogiro
      autogiroCustomerNr: '',
      autogiroAccount: '',

      // Övriga betalmetodinställningar
      paymentMethod: 0,
      paymentMethodOrder: '',
      pmSelectionHeader: '',
      preSelect: false,
      autoApprovePayments: false,
      sharedAccount: false,

      // Kortdata
      cardData1: '',
      cardData2: '',
      cardData3: '',
      cardSupplier: 0,

      // Avgifter / avtal
      adminFee: 0,

      // Övriga metadata
      customerNr: '',
      name: '',
      owner_id: 0,
      localAssIds: '',
      payerNumberIsBirthdate: false,
      continueBtnText: '',
      senderTitle: '',
      template_id: 0,
      paymentMethods: [],
    },
  });
  const { handleSubmit, watch, setValue, reset } = formMethods;

  function associationAccountTypeDescription(type: AssociationAccountTypeEnum): string {
    return getAssociationAccountTypeDescription(type, t);
  }

  const paymentMethod = watch('paymentMethod');
  const isSwishActive = hasFlag(paymentMethod, AvailablePaymentMethodsEnum.Swish);
  const isStripeActive = hasFlag(paymentMethod, AvailablePaymentMethodsEnum.Card);

  useEffect(() => {
    const accountType = watch('accountType') || AvailablePaymentMethodsEnum.NotSet;
    const accountName = associationAccountTypeDescription(accountType);
    const setStripeStatus = isStripeActive ? '1' : '0';
    const setSwishStatus = isSwishActive ? '1' : '0';

    if (watch('stripeActive') !== setStripeStatus) {
      setValue('stripeActive', setStripeStatus);
    }
    if (watch('swishActive') !== setSwishStatus) {
      setValue('swishActive', setSwishStatus);
    }
    if (watch('accountName') !== accountName) {
      setValue('accountName', accountName);
    }
  }, [isStripeActive, isSwishActive, setValue, watch]);

  const fetchInvoiceGroup = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        const result = await getInvoiceGroupById(id);
        if (!isMounted()) return;

        const paymentMethodsSorted = [...(result.data.paymentMethods ?? [])].sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0));
        const normalized = {
          ...result.data,
          paymentMethods: paymentMethodsSorted,
          accountName: '',
          autogiroAccount: result.data.autogiroAccount ?? '',
        };

        setInvoiceGroup(normalized);
        reset(normalized);
      } catch (err) {
        if (isMounted()) ConsoleHelper.log(err);
      } finally {
        if (isMounted()) setLoading(false);
      }
    },
    [isMounted, reset],
  );

  useEffect(() => {
    if (!params.id) return;
    fetchInvoiceGroup(+params.id);
  }, [params.id, fetchInvoiceGroup]);

  const refetchInvoiceGroup = useCallback(() => {
    if (!params.id) return;
    fetchInvoiceGroup(Number(params.id));
  }, [params.id, fetchInvoiceGroup]);

  const onSubmit = useCallback(
    async (data: InvoiceGroupInterface) => {
      const dataToPost: InvoiceGroupInterface = {
        ...data,
      };

      ConsoleHelper.log('dataToPost', dataToPost);
      try {
        if (isMounted()) {
          // const res = await updateFaktura-grupp(dataToPost);
          // toast.success(t('invoice:adminInvoiceGroupDetailPage.saveSuccess'));
          // refetchInvoiceGroup();
        }
      } catch (e) {
        if (isMounted()) ConsoleHelper.log('error', e);
      }
    },
    [isMounted],
  );

  return (
    <GridWrapper>
      <Grid item xs='auto' sx={{ display: 'flex', alignItems: 'center' }}>
        <MainContentHeader title={invoiceGroup?.name || ''} />
      </Grid>
      <Grid item xs={12}>
        <FormProvider {...formMethods}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Paper>
              <LoadingOverlay loading={loading} />
              <Tabs value={0}>
                <Tab label={t('invoice:adminInvoiceGroupDetailPage.paymentMethods')} value={0} />
              </Tabs>
              <Divider />
              <Box sx={{ p: 3 }}>
                {!loading && invoiceGroup && (
                  <>
                    <Grid container spacing={5}>
                      <Grid item xs={12} sm={5} md={6} lg={7}>
                        <PaymentMethodsConfiguration invoiceGroup={invoiceGroup} refetchInvoiceGroup={refetchInvoiceGroup} />
                      </Grid>

                      <Grid item xs={12} sm={7} md={6} lg={5}>
                        <ActivePaymentOptionsInfo />
                      </Grid>
                    </Grid>

                    {/* PUT - Ej möjligt för tillfället */}
                    {/* <Grid item xs={12} mt={3}>
                      <Divider />
                    </Grid>
                    <Grid item xs={12} display='flex' justifyContent='flex-end' mt={2}>
                      <Button variant='contained' size='small' disableElevation type='submit' disabled={loading}>
                        {t('common:button.save')}
                      </Button>
                    </Grid> */}
                  </>
                )}
              </Box>
            </Paper>
          </form>
        </FormProvider>
      </Grid>
    </GridWrapper>
  );
};
